import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

/**
 * SettingsContext — fetches /api/settings once and exposes {settings, refresh}.
 * Every component can useSettings() to read the current phone/whatsapp_number
 * without hardcoding them. Admin's PATCH /api/admin/settings then triggers
 * refresh() so the whole app picks up the new numbers instantly.
 */
const SettingsContext = createContext({ settings: {}, refresh: () => {} });

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({});

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings(data || {});
    } catch { /* keep last-known good */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <SettingsContext.Provider value={{ settings, refresh }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => useContext(SettingsContext);

/** Normalise a raw phone/WA number to digits-only (E.164 without the +). */
export const digits = (v) => (v || "").replace(/\D/g, "");

/** Build a wa.me link from a settings snapshot with an optional pre-filled message. */
export const waLink = (waNumber, message = "") => {
  const num = digits(waNumber);
  if (!num) return "";
  const suffix = message ? `?text=${encodeURIComponent(message)}` : "";
  return `https://wa.me/${num}${suffix}`;
};
